import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, MessageCircle, Shield, Zap, ChevronRight,
  Smartphone, ShoppingBag, Apple, Home, Heart, Dumbbell, Briefcase,
  Package, ArrowRight, Phone, CheckCircle, Star, MapPin, ChevronDown,
  Truck, BadgeCheck,
} from "lucide-react";
import API, { getImageUrl } from "../api";
import { formatPrice, StarRating } from "../components/ProductCard";
import { formatWhatsApp } from "../utils/whatsapp";

const ADMIN_WA = "33777369462";

// Icônes par slug de catégorie
const CAT_ICONS = {
  "electronique":      { icon: Smartphone, bg: "bg-blue-50",    text: "text-blue-600",   border: "border-blue-100"   },
  "mode-vetements":    { icon: ShoppingBag, bg: "bg-pink-50",   text: "text-pink-600",   border: "border-pink-100"   },
  "alimentation":      { icon: Apple,       bg: "bg-emerald-50",text: "text-emerald-600",border: "border-emerald-100"},
  "maison-decoration": { icon: Home,        bg: "bg-amber-50",  text: "text-amber-600",  border: "border-amber-100"  },
  "beaute-sante":      { icon: Heart,       bg: "bg-rose-50",   text: "text-rose-600",   border: "border-rose-100"   },
  "sports-loisirs":    { icon: Dumbbell,    bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100" },
  "services":          { icon: Briefcase,   bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-100" },
  "autres":            { icon: Package,     bg: "bg-gray-50",   text: "text-gray-500",   border: "border-gray-100"   },
};

const TRUST = [
  { icon: BadgeCheck, title: "Vendeurs vérifiés",     desc: "Chaque vendeur est validé manuellement avant publication." },
  { icon: MessageCircle, title: "Contact instantané", desc: "Répondez directement via WhatsApp, sans intermédiaire." },
  { icon: Truck,      title: "Livraison négociable",   desc: "Conditions discutées directement avec le vendeur." },
  { icon: Phone,      title: "Paiement direct",        desc: "Wave, Orange Money — sans commission cachée." },
];

export default function HomePage() {
  const [categories,   setCategories]   = useState([]);
  const [nouveautes,   setNouveautes]   = useState([]);
  const [populaires,   setPopulaires]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [publicStats,  setPublicStats]  = useState(null);
  const productsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      API.get("/api/categories"),
      API.get("/api/products?limit=8"),
      API.get("/api/products/popular?limit=6"),
      API.get("/api/stats/public"),
    ])
      .then(([cats, newest, popular, stats]) => {
        setCategories(cats.data || []);
        setNouveautes(newest.data.products || []);
        setPopulaires(popular.data || []);
        setPublicStats(stats.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/catalogue?search=${encodeURIComponent(search.trim())}`);
  };

  const scrollToProducts = () => {
    productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white pb-20 md:pb-0 overflow-x-hidden">

      {/* ══════════ HERO COMPACT ══════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #14532d 0%, #16A34A 55%, #15803d 100%)" }}
        data-testid="hero-section"
      >
        {/* Décoration lumineuse */}
        <div className="pointer-events-none absolute top-0 right-1/3 w-80 h-80 bg-emerald-400 rounded-full opacity-10 blur-3xl" />

        {/* IMAGE desktop — droite */}
        <div className="absolute right-0 top-0 bottom-0 w-[40%] sm:w-[45%] block overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1752486268325-c562c3fb5467?auto=format&fit=crop&crop=faces,center&w=700&h=700&q=75"
            alt="Vendeuse Jokkoleen"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to right, #16A34A 0%, rgba(22,163,74,0.7) 20%, transparent 60%)" }}
          />
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 pt-7 pb-8 sm:py-12">
          <div className="max-w-lg lg:max-w-xl">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] inline-block" />
              Marketplace sénégalaise · WhatsApp-first
            </div>

            {/* H1 compact */}
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-2"
              data-testid="hero-title"
            >
              Les bonnes affaires
              <br />
              <span className="text-[#F59E0B]">au Sénégal !</span>
            </h1>
            <p className="text-green-100 text-sm sm:text-base leading-relaxed mb-5">
              Découvrez des produits proposés par nos vendeurs et commandez simplement.
            </p>

            {/* Barre de recherche */}
            <form onSubmit={handleSearch} className="flex mb-4 rounded-xl overflow-hidden shadow-lg" data-testid="hero-search-form">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un produit ou service…"
                className="flex-1 px-4 py-3 text-gray-800 text-sm focus:outline-none bg-white min-w-0"
                data-testid="hero-search-input"
              />
              <button type="submit" className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold px-5 py-3 transition-colors shrink-0">
                <Search size={18} />
              </button>
            </form>

            {/* CTAs */}
            <div className="flex flex-wrap gap-2.5 mb-5">
              <button
                onClick={scrollToProducts}
                className="flex items-center gap-2 bg-white text-[#16A34A] font-bold px-5 py-2.5 rounded-xl hover:bg-green-50 transition-all text-sm shadow-md hover:-translate-y-0.5"
                data-testid="hero-discover-btn"
              >
                Découvrir les produits <ChevronDown size={15} />
              </button>
              <a
                href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent("Bonjour, je cherche un vendeur sur Jokkoleen !")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/15 border border-white/25 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-white/25 text-sm"
                data-testid="hero-whatsapp-cta"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-6">
              {[
                { val: publicStats ? String(publicStats.total_vendors)  : "…", label: "Vendeurs" },
                { val: publicStats ? String(publicStats.total_products) : "…", label: "Produits" },
                { val: "Gratuit", label: "Pour démarrer" },
              ].map(({ val, label }) => (
                <div key={label}>
                  <p className="font-black text-white text-xl leading-none">{val}</p>
                  <p className="text-[11px] text-green-200 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ TRUST STRIP ══════════ */}
      <div className="bg-[#F0FDF4] border-b border-green-100">
        <div className="w-full max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-4 text-xs text-green-800 font-semibold overflow-x-auto">
          <span className="flex items-center gap-1.5 shrink-0">
            <div className="w-4 h-4 rounded-full bg-[#1CBBEE] flex items-center justify-center">
              <span className="text-white font-black text-[8px]">W</span>
            </div>Wave
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            <div className="w-4 h-4 rounded-full bg-[#FF6600] flex items-center justify-center">
              <span className="text-white font-black text-[8px]">O</span>
            </div>Orange Money
          </span>
          <span className="flex items-center gap-1.5 shrink-0"><CheckCircle size={12} className="text-[#16A34A]" /> Vendeurs vérifiés</span>
          <span className="flex items-center gap-1.5 shrink-0"><Shield size={12} className="text-[#16A34A]" /> Paiement direct</span>
          <a
            href={`https://wa.me/${ADMIN_WA}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1 text-[#16A34A] font-bold hover:underline shrink-0"
          >
            <MessageCircle size={12} /> Support
          </a>
        </div>
      </div>

      {/* ══════════ CATÉGORIES ══════════ */}
      <section className="w-full max-w-6xl mx-auto px-4 pt-6 pb-2" data-testid="categories-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black text-gray-900">Catégories</h2>
          <Link to="/catalogue" className="flex items-center gap-1 text-[#16A34A] font-semibold text-xs hover:underline">
            Tout voir <ChevronRight size={14} />
          </Link>
        </div>
        {/* Scroll horizontal sur mobile, grille sur desktop */}
        <div
          className="flex gap-2.5 overflow-x-auto pb-2 sm:grid sm:grid-cols-8 sm:overflow-visible scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {(categories.length > 0 ? categories : Object.keys(CAT_ICONS).map(s => ({ slug: s, name: s }))).map((cat) => {
            const style = CAT_ICONS[cat.slug] || CAT_ICONS["autres"];
            const Icon  = style.icon;
            return (
              <Link
                key={cat.slug}
                to={`/catalogue?category=${cat.slug}`}
                className={`flex flex-col items-center gap-1.5 p-2.5 bg-white rounded-2xl border ${style.border} hover:shadow-md transition-all duration-200 shrink-0 w-[70px] sm:w-auto`}
                data-testid={`category-${cat.slug}`}
              >
                <div className={`w-10 h-10 rounded-xl ${style.bg} flex items-center justify-center`}>
                  <Icon size={18} className={style.text} />
                </div>
                <span className="text-[10px] font-semibold text-center text-gray-700 leading-tight line-clamp-2">
                  {cat.name}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ══════════ SECTIONS PRODUITS ══════════ */}
      <div ref={productsRef} id="products-section" className="bg-[#F9FAFB] pt-5 pb-8 mt-4">
        <div className="w-full max-w-6xl mx-auto px-4 space-y-10">

          {/* ─── NOUVEAUTÉS ─── */}
          <section data-testid="nouveautes-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">🆕</span>
                <h2 className="text-lg font-black text-gray-900">Nouveautés</h2>
              </div>
              <Link to="/catalogue" className="flex items-center gap-1 text-[#16A34A] font-semibold text-xs hover:underline">
                Voir tout <ChevronRight size={14} />
              </Link>
            </div>

            {loading ? (
              <ProductGridSkeleton />
            ) : nouveautes.length === 0 ? (
              <EmptyProducts navigate={navigate} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {nouveautes.map((p) => <HomeProductCard key={p._id} product={p} />)}
              </div>
            )}
          </section>

          {/* ─── PRODUITS POPULAIRES (masqué si pas de données) ─── */}
          {!loading && populaires.length >= 3 && (
            <section data-testid="populaires-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⭐</span>
                  <h2 className="text-lg font-black text-gray-900">Produits populaires</h2>
                </div>
                <Link to="/catalogue" className="flex items-center gap-1 text-[#16A34A] font-semibold text-xs hover:underline">
                  Voir tout <ChevronRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {populaires.slice(0, 8).map((p) => <HomeProductCard key={p._id} product={p} badge="Populaire" />)}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* ══════════ RÉASSURANCE ══════════ */}
      <section className="bg-white py-10">
        <div className="w-full max-w-6xl mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-lg font-black text-gray-900">Pourquoi Jokkoleen ?</h2>
            <p className="text-gray-500 text-xs mt-1">Simple, direct, local</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {TRUST.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#F9FAFB] rounded-2xl p-4 border border-gray-100">
                <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center mb-3">
                  <Icon size={17} className="text-[#16A34A]" />
                </div>
                <h3 className="font-bold text-gray-900 text-xs mb-1">{title}</h3>
                <p className="text-gray-500 text-[11px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ COMMENT ÇA MARCHE ══════════ */}
      <section className="bg-[#F0FDF4] border-y border-green-100 py-10" data-testid="how-it-works">
        <div className="w-full max-w-6xl mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-lg font-black text-gray-900">Comment commander ?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { num: "1", icon: Search,        title: "Cherchez",   desc: "Parcourez le catalogue et trouvez ce qui vous intéresse." },
              { num: "2", icon: MessageCircle, title: "Contactez",  desc: "Cliquez sur WhatsApp pour parler directement au vendeur." },
              { num: "3", icon: CheckCircle,   title: "Réglez",     desc: "Payez en direct par Wave ou Orange Money, sans commission." },
            ].map(({ num, icon: Icon, title, desc }, i) => (
              <div key={num} className="relative flex flex-col items-center text-center gap-3 p-5 bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-[#16A34A] to-[#166534] rounded-xl flex items-center justify-center shadow">
                  <Icon size={22} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#16A34A] mb-0.5">Étape {num}</p>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                </div>
                {i < 2 && <div className="hidden sm:block absolute -right-2 top-1/2 -translate-y-1/2 z-10"><ArrowRight size={18} className="text-green-200" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CTA VENDEUR ══════════ */}
      <section className="bg-white py-10">
        <div className="w-full max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#166534] to-[#16A34A] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-center sm:text-left">
              <h2 className="text-lg sm:text-xl font-black text-white mb-1">Vous vendez quelque chose ?</h2>
              <p className="text-green-200 text-sm">Rejoignez les premiers vendeurs — commencez gratuitement.</p>
              <div className="flex flex-wrap gap-3 mt-3 justify-center sm:justify-start text-xs text-green-300 font-medium">
                <span className="flex items-center gap-1"><CheckCircle size={10} /> Plan FREE</span>
                <span className="flex items-center gap-1"><CheckCircle size={10} /> Sans engagement</span>
                <span className="flex items-center gap-1"><CheckCircle size={10} /> Support WhatsApp</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button
                onClick={() => navigate("/auth?mode=register&role=vendor")}
                className="bg-white text-[#16A34A] font-bold px-5 py-3 rounded-xl hover:bg-green-50 transition-all text-sm whitespace-nowrap shadow hover:-translate-y-0.5"
                data-testid="become-vendor-cta"
              >
                Créer ma boutique
              </button>
              <a
                href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent("Bonjour, je veux vendre sur Jokkoleen")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold px-5 py-3 rounded-xl hover:bg-[#20BD5A] text-sm whitespace-nowrap hover:-translate-y-0.5 transition-all"
                data-testid="vendor-whatsapp-cta"
              >
                <MessageCircle size={15} /> Via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="bg-gray-900 text-gray-400 py-10">
        <div className="w-full max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
            <div className="col-span-2 sm:col-span-1">
              <img src="/jokkoleen-logo-header.png" alt="Jokkoleen" className="h-10 w-auto mb-3 opacity-90" />
              <p className="text-xs leading-relaxed text-gray-500">
                La marketplace WhatsApp-first du Sénégal. Vendeurs locaux, produits authentiques.
              </p>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Plateforme</p>
              <ul className="space-y-2 text-xs">
                <li><Link to="/catalogue" className="hover:text-green-400 transition-colors">Explorer</Link></li>
                <li><Link to="/auth?mode=register&role=vendor" className="hover:text-green-400 transition-colors">Devenir vendeur</Link></li>
                <li><Link to="/auth" className="hover:text-green-400 transition-colors">Connexion</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Catégories</p>
              <ul className="space-y-2 text-xs">
                {categories.slice(0, 4).map((c) => (
                  <li key={c.slug}>
                    <Link to={`/catalogue?category=${c.slug}`} className="hover:text-green-400 transition-colors">{c.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Assistance</p>
              <a
                href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent("Bonjour Jokkoleen, j'ai besoin d'aide")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-4 py-2 rounded-xl text-xs hover:bg-[#20BD5A] transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={12} /> WhatsApp Support
              </a>
              <p className="text-xs text-gray-600 mt-2">Lun–Sam · 8h–20h</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-5 text-xs text-gray-600 text-center">
            © 2025 Jokkoleen · Marketplace Sénégal · Tous droits réservés
          </div>
        </div>
      </footer>

    </div>
  );
}

/* ─── Skeleton loading ─── */
function ProductGridSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse border border-gray-100">
          <div className="aspect-square bg-gray-100" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-gray-100 rounded-full" />
            <div className="h-3 bg-gray-100 rounded-full w-2/3" />
            <div className="h-8 bg-gray-100 rounded-xl mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Empty state ─── */
function EmptyProducts({ navigate }) {
  return (
    <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
      <Package size={48} className="text-gray-200 mx-auto mb-3" />
      <p className="text-gray-500 font-semibold text-sm mb-1">Aucun produit disponible</p>
      <p className="text-gray-400 text-xs mb-5">Soyez le premier vendeur sur Jokkoleen !</p>
      <button
        onClick={() => navigate("/auth?mode=register&role=vendor")}
        className="bg-[#16A34A] text-white font-bold px-5 py-2 rounded-xl hover:bg-[#166534] transition-colors text-sm"
      >
        Devenir vendeur
      </button>
    </div>
  );
}

/* ─── Carte produit homepage ─── */
function HomeProductCard({ product, badge }) {
  const imgSrc = getImageUrl(product.images?.[0]) || "https://via.placeholder.com/300x300?text=Photo";
  const waLink = product.vendor_whatsapp
    ? `https://wa.me/${formatWhatsApp(product.vendor_whatsapp)}?text=${encodeURIComponent(`Bonjour, je vous contacte depuis Jokkoleen au sujet du produit "${product.name}". Est-il toujours disponible ?`)}`
    : null;
  return (
    <div
      className="bg-white rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 border border-gray-100 flex flex-col"
      data-testid="home-product-card"
    >
      <Link to={`/produit/${product._id}`} className="relative block">
        <div className="aspect-square overflow-hidden bg-gray-100">
          <img
            src={imgSrc}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=Photo"; }}
          />
        </div>
        {badge && (
          <span className="absolute top-2 left-2 bg-[#F59E0B] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <Link to={`/produit/${product._id}`}>
          <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-2 hover:text-[#16A34A] transition-colors mb-1 leading-snug">
            {product.name}
          </h3>
        </Link>
        {product.vendor_shop_name && (
          <p className="text-[10px] text-gray-400 flex items-center gap-0.5 mb-1 truncate">
            <MapPin size={8} /> {product.vendor_shop_name}
          </p>
        )}
        {product.total_reviews > 0 && (
          <div className="flex items-center gap-1 mb-1">
            <StarRating rating={product.rating} size={9} />
            <span className="text-[10px] text-gray-400">({product.total_reviews})</span>
          </div>
        )}
        <p className="font-black text-gray-900 text-sm mb-2 mt-auto">{formatPrice(product.price)}</p>
        {/* Prix original si promo */}
        {product.original_price && product.original_price > product.price && (
          <p className="text-[10px] text-gray-400 line-through -mt-1.5 mb-2">{formatPrice(product.original_price)}</p>
        )}
        {waLink ? (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#25D366] text-white font-bold py-2 rounded-xl text-xs hover:bg-[#20BD5A] transition-colors flex items-center justify-center gap-1.5 mt-auto"
            data-testid="contact-product-btn"
          >
            <MessageCircle size={12} /> Commander
          </a>
        ) : (
          <Link
            to={`/produit/${product._id}`}
            className="w-full bg-[#16A34A] text-white font-bold py-2 rounded-xl text-xs hover:bg-[#166534] transition-colors flex items-center justify-center gap-1.5 mt-auto"
            data-testid="contact-product-btn"
          >
            Voir le produit <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}
